import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  DROPBOX_OFF,
  disconnectDropbox,
  dropboxStartFolder,
  finishDropboxConnect,
  getDropboxStatus,
  setDropboxFolder,
  startDropboxConnect,
  type DropboxReturn,
  type DropboxStatus,
} from "../shared/dropboxClient";

import DropboxFolderPicker from "./components/DropboxFolderPicker";

// The return from Dropbox is finished once per page load, even if Settings
// unmounts and mounts again (tab switches).
let dropboxReturnHandled = false;

/* ============================================================
   MobileSettingsTab
   ----------------------------------------------------------------
   Bottom-tab content for "Settings" on mobile. Round 1 was a
   "coming soon" placeholder; this round adds a real Log Out
   action so signed-in users can sign out without going to desktop.

   Editable settings (name / phone / email / logo / signature /
   templates / sections) still live on the desktop Settings page —
   the userSettings shown here are read-only and just give the
   user something concrete to recognize so the Log Out doesn't
   feel orphaned.

   Sign-out flow: supabase.auth.signOut() → MobileApp's
   onAuthStateChange subscription catches SIGNED_OUT → session
   becomes null → the auth gate at the top of MobileApp returns
   <MobileAuthPage />. No router push, no manual nav.
   ============================================================ */

type Props = {
  userEmail?: string | null;
  userName?: string | null;
  companyName?: string | null;
  /** Set when the page loaded on the way back from Dropbox's Allow page. */
  dropboxReturn?: DropboxReturn | null;
};

type Status =
  | { kind: "idle" }
  | { kind: "confirming" }
  | { kind: "signingOut" }
  | { kind: "error"; message: string };

export default function MobileSettingsTab({
  userEmail,
  userName,
  companyName,
  dropboxReturn = null,
}: Props) {
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  // ---------------- Dropbox ----------------
  const [dbx, setDbx] = useState<DropboxStatus | null>(null);
  const [dbxBusy, setDbxBusy] = useState<"" | "connect" | "finish" | "folder" | "disconnect">("");
  const [dbxMsg, setDbxMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const applyStatus = (s: DropboxStatus) => setDbx(s);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ret = !dropboxReturnHandled ? dropboxReturn : null;
      dropboxReturnHandled = true;
      if (ret?.kind === "code") {
        setDbxBusy("finish");
        const r = await finishDropboxConnect(supabase, ret.code, ret.state);
        if (cancelled) return;
        setDbxBusy("");
        if (r.ok) {
          applyStatus(r.data);
          setDbxMsg({ ok: true, text: "Dropbox connected. Save to Dropbox is on the Estimate screen." });
          // First thing after connecting: choose where files go, so they
          // don't quietly land in a new folder at the top of their Dropbox.
          setPickerOpen(true);
          return;
        }
        setDbxMsg({ ok: false, text: r.error });
      } else if (ret?.kind === "denied") {
        setDbxMsg({ ok: false, text: "Dropbox wasn't connected (Allow was not tapped)." });
      } else if (ret?.kind === "error") {
        setDbxMsg({ ok: false, text: "Dropbox didn't finish connecting. Try again." });
      }
      const s = await getDropboxStatus(supabase);
      if (!cancelled) applyStatus(s);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConnect = async () => {
    setDbxBusy("connect");
    setDbxMsg(null);
    const r = await startDropboxConnect(supabase);
    // On success the page is leaving for Dropbox; only failures land here.
    if (!r.ok) {
      setDbxBusy("");
      setDbxMsg({ ok: false, text: r.error });
    }
  };

  const onPickFolder = async (pick: {
    pickedPath: string;
    perJobFolder: boolean;
    rollYear: boolean;
  }) => {
    setDbxBusy("folder");
    setDbxMsg(null);
    const r = await setDropboxFolder(supabase, pick);
    setDbxBusy("");
    if (r.ok) {
      applyStatus(r.data);
      setPickerOpen(false);
      setDbxMsg({ ok: true, text: "Folder saved." });
    } else {
      setDbxMsg({ ok: false, text: r.error });
      setPickerOpen(false);
    }
  };

  const onDisconnect = async () => {
    setDbxBusy("disconnect");
    setDbxMsg(null);
    const r = await disconnectDropbox(supabase);
    setDbxBusy("");
    setConfirmDisconnect(false);
    if (r.ok) {
      applyStatus(r.data);
      setDbxMsg({ ok: true, text: "Dropbox disconnected." });
    } else {
      setDbxMsg({ ok: false, text: r.error });
    }
  };

  const dbxView = dbx || DROPBOX_OFF;
  // Folder the picker opens on: the current save folder.
  const pickerStart = dropboxStartFolder(dbxView);

  const askLogout = () => setStatus({ kind: "confirming" });
  const cancelLogout = () => setStatus({ kind: "idle" });

  const performLogout = async () => {
    setStatus({ kind: "signingOut" });
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      // Don't reset status — the auth gate will unmount this whole
      // settings screen as soon as SIGNED_OUT fires and session flips
      // to null. (MobileApp.tsx subscribes to onAuthStateChange.)
    } catch (err: any) {
      setStatus({
        kind: "error",
        message: err?.message || "Sign out failed. Try again.",
      });
    }
  };

  return (
    <main className="m-content m-settings" aria-label="Settings">
      <section className="m-card m-settings-account" aria-label="Account">
        <h3 className="m-card-title">Account</h3>
        <div className="m-settings-row">
          <span className="m-settings-row-label">Signed in as</span>
          <span className="m-settings-row-value">
            {(userEmail || "").trim() || "—"}
          </span>
        </div>
        {(userName || "").trim() ? (
          <div className="m-settings-row">
            <span className="m-settings-row-label">Name</span>
            <span className="m-settings-row-value">{userName}</span>
          </div>
        ) : null}
        {(companyName || "").trim() ? (
          <div className="m-settings-row">
            <span className="m-settings-row-label">Company</span>
            <span className="m-settings-row-value">{companyName}</span>
          </div>
        ) : null}
      </section>

      {dbx === null || dbxView.available || dbxMsg ? (
        <section className="m-card m-settings-dropbox" aria-label="Dropbox">
          <h3 className="m-card-title">Dropbox</h3>
          {dbx === null || dbxBusy === "finish" ? (
            <p className="m-settings-help">
              {dbxBusy === "finish" ? "Finishing the Dropbox connection…" : "Checking Dropbox…"}
            </p>
          ) : !dbxView.available ? null : !dbxView.connected ? (
            <>
              <p className="m-settings-help">
                Connect your own Dropbox to save estimates from this phone with
                one tap. You'll sign in to Dropbox once and tap Allow.
              </p>
              <button
                type="button"
                className="m-settings-logout m-settings-primary"
                disabled={!!dbxBusy}
                onClick={onConnect}
              >
                {dbxBusy === "connect" ? "Opening Dropbox…" : "Connect Dropbox"}
              </button>
            </>
          ) : (
            <>
              <div className="m-settings-row">
                <span className="m-settings-row-label">Connected</span>
                <span className="m-settings-row-value">
                  {dbxView.accountName || "Dropbox"}
                  {dbxView.accountEmail ? (
                    <span className="m-settings-subvalue">{dbxView.accountEmail}</span>
                  ) : null}
                </span>
              </div>

              <div className="m-settings-field">
                <span className="m-settings-row-label">Saves to</span>
                <span className="m-settings-path m-settings-path--block">
                  {dbxView.examplePath.split("/").filter(Boolean).join(" › ")}
                </span>
                <span className="m-settings-help m-settings-help--small">
                  Example for a job in Woodbury for Sabo.
                </span>
              </div>
              <button
                type="button"
                className="m-settings-logout m-settings-primary"
                disabled={!!dbxBusy}
                onClick={() => setPickerOpen(true)}
              >
                Choose folder
              </button>

              {confirmDisconnect ? (
                <div className="m-settings-confirm-row">
                  <button
                    type="button"
                    className="m-settings-cancel"
                    onClick={() => setConfirmDisconnect(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="m-settings-logout m-settings-logout--confirm"
                    disabled={!!dbxBusy}
                    onClick={onDisconnect}
                  >
                    {dbxBusy === "disconnect" ? "Disconnecting…" : "Yes, disconnect"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="m-settings-logout"
                  disabled={!!dbxBusy}
                  onClick={() => setConfirmDisconnect(true)}
                >
                  Disconnect Dropbox
                </button>
              )}
            </>
          )}
          {dbxMsg ? (
            <div
              className={`m-auth-msg ${dbxMsg.ok ? "m-auth-msg--info" : "m-auth-msg--err"}`}
              role={dbxMsg.ok ? "status" : "alert"}
            >
              {dbxMsg.text}
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="m-card m-settings-info" aria-label="More settings">
        <h3 className="m-card-title">Profile & branding</h3>
        <p className="m-settings-help">
          Manage your name, phone, email, logo, signature, and proposal
          sections from the desktop app at{" "}
          <span className="m-settings-help-accent">
            proposals.estimator.trade
          </span>
          . Changes you make there sync to this device automatically.
        </p>
      </section>

      <section className="m-card m-settings-danger" aria-label="Sign out">
        <h3 className="m-card-title">Session</h3>

        {status.kind === "confirming" ? (
          <>
            <p className="m-settings-help">
              You'll be signed out on this device. Your saved estimates
              and proposals stay in the cloud — log back in any time.
            </p>
            <div className="m-settings-confirm-row">
              <button
                type="button"
                className="m-settings-cancel"
                onClick={cancelLogout}
              >
                Cancel
              </button>
              <button
                type="button"
                className="m-settings-logout m-settings-logout--confirm"
                onClick={performLogout}
              >
                Yes, log out
              </button>
            </div>
          </>
        ) : status.kind === "signingOut" ? (
          <button type="button" className="m-settings-logout" disabled>
            Signing out…
          </button>
        ) : (
          <>
            <button
              type="button"
              className="m-settings-logout"
              onClick={askLogout}
            >
              Log Out
            </button>
            {status.kind === "error" ? (
              <div className="m-auth-msg m-auth-msg--err" role="alert">
                {status.message}
              </div>
            ) : null}
          </>
        )}
      </section>
      {pickerOpen ? (
        <DropboxFolderPicker
          startPath={pickerStart}
          busy={dbxBusy === "folder"}
          onCancel={() => setPickerOpen(false)}
          onPick={onPickFolder}
        />
      ) : null}
    </main>
  );
}
